import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Plus,
  Copy,
  Eye,
  EyeOff,
  QrCode,
  Download,
  Trash2,
  Ticket,
  CheckCircle,
  AlertCircle,
  CheckSquare,
  Square,
  Trash,
  FileText,
  Printer,
  X,
  Search,
  Filter,
} from 'lucide-react';
import PaginationControls from '../../components/admin/PaginationControls';

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  const [selectedVouchers, setSelectedVouchers] = useState(new Set());
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [printSettings, setPrintSettings] = useState({
    hotelName: 'Sateva Hotspot',
    selectedOnly: false,
  });
  const [formData, setFormData] = useState({
    plan_id: '',
    count: 1,
    expires_at: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, planFilter, pageSize]);

  const loadData = async () => {
    try {
      const [vouchersResponse, plansResponse] = await Promise.all([
        adminAPI.getVouchers(),
        adminAPI.getPlans(),
      ]);
      setVouchers(vouchersResponse.data);
      setPlans(plansResponse.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(error.response?.data?.error || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const filteredVouchers = useMemo(() => {
    return vouchers.filter((voucher) => {
      const matchesSearch = !searchTerm || [
        voucher.code,
        voucher.plan_name,
        voucher.used_by_name,
        voucher.status,
      ].some((value) => String(value || '').toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || voucher.status === statusFilter;
      const matchesPlan = planFilter === 'all' || String(voucher.plan_id) === planFilter;

      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [vouchers, searchTerm, statusFilter, planFilter]);

  const paginatedVouchers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredVouchers.slice(start, start + pageSize);
  }, [filteredVouchers, page, pageSize]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        plan_id: parseInt(formData.plan_id, 10),
        count: parseInt(formData.count, 10),
        expires_at: formData.expires_at || null,
      };

      await adminAPI.createVouchers(data);
      toast.success(`${data.count} voucher(s) created successfully`);
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to create vouchers:', error);
      toast.error(error.response?.data?.error || 'Failed to create vouchers');
    }
  };

  const resetForm = () => {
    setFormData({
      plan_id: '',
      count: 1,
      expires_at: '',
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Code copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const downloadQR = async (voucherId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/download/voucher-qr/${voucherId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `voucher-qr-${voucherId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('QR code downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download QR code');
    }
  };

  const deleteVoucher = async (voucherId, voucherCode) => {
    if (!window.confirm(`Are you sure you want to delete voucher "${voucherCode}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminAPI.deleteVoucher(voucherId);
      toast.success(response.data?.message || `Voucher "${voucherCode}" deleted successfully`);
      loadData();
    } catch (error) {
      console.error('Failed to delete voucher:', error);
      toast.error(error.response?.data?.error || `Failed to delete voucher "${voucherCode}"`);
    }
  };

  const toggleVoucherSelection = (voucherId) => {
    const newSelected = new Set(selectedVouchers);
    if (newSelected.has(voucherId)) {
      newSelected.delete(voucherId);
    } else {
      newSelected.add(voucherId);
    }
    setSelectedVouchers(newSelected);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedVouchers.map((voucher) => voucher.id);
    const allSelected = pageIds.every((voucherId) => selectedVouchers.has(voucherId));
    const nextSelected = new Set(selectedVouchers);

    if (allSelected) {
      pageIds.forEach((voucherId) => nextSelected.delete(voucherId));
    } else {
      pageIds.forEach((voucherId) => nextSelected.add(voucherId));
    }

    setSelectedVouchers(nextSelected);
  };

  const bulkDeleteVouchers = async () => {
    if (selectedVouchers.size === 0) {
      toast.error('Please select vouchers to delete');
      return;
    }

    const selectedVoucherDetails = vouchers.filter((voucher) => selectedVouchers.has(voucher.id));
    const totalCount = selectedVoucherDetails.length;

    let message = `Are you sure you want to delete ${totalCount} voucher${totalCount > 1 ? 's' : ''}?`;
    message += '\n\nThis action cannot be undone.';

    if (!window.confirm(message)) {
      return;
    }

    try {
      const deletePromises = selectedVoucherDetails.map(async (voucher) => {
        try {
          await adminAPI.deleteVoucher(voucher.id);
          return { success: true, code: voucher.code };
        } catch (error) {
          console.error(`Failed to delete voucher ${voucher.id}:`, error);
          return {
            success: false,
            code: voucher.code,
            message: error.response?.data?.error || 'Delete failed',
          };
        }
      });

      const results = await Promise.all(deletePromises);
      const successful = results.filter((result) => result.success);
      const failed = results.filter((result) => !result.success);

      if (successful.length > 0) {
        toast.success(`${successful.length} voucher${successful.length > 1 ? 's' : ''} deleted successfully`);
      }
      if (failed.length > 0) {
        toast.error(failed[0].message || `Failed to delete ${failed.length} voucher${failed.length > 1 ? 's' : ''}`);
      }

      setSelectedVouchers(new Set());
      loadData();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete vouchers');
    }
  };

  const deleteAllVouchers = async () => {
    if (filteredVouchers.length === 0) {
      toast.error('No vouchers to delete');
      return;
    }

    let message = `Are you sure you want to delete ${filteredVouchers.length} voucher${filteredVouchers.length > 1 ? 's' : ''} in the current result set?`;
    message += '\n\nThis action cannot be undone.';

    if (!window.confirm(message)) {
      return;
    }

    try {
      const deletePromises = filteredVouchers.map(async (voucher) => {
        try {
          await adminAPI.deleteVoucher(voucher.id);
          return { success: true };
        } catch (error) {
          return {
            success: false,
            message: error.response?.data?.error || 'Delete failed',
          };
        }
      });

      const results = await Promise.all(deletePromises);
      const successful = results.filter((result) => result.success);
      const failed = results.filter((result) => !result.success);

      if (successful.length > 0) {
        toast.success(`${successful.length} voucher${successful.length > 1 ? 's' : ''} deleted successfully`);
      }
      if (failed.length > 0) {
        toast.error(failed[0].message || `Failed to delete ${failed.length} voucher${failed.length > 1 ? 's' : ''}`);
      }

      setSelectedVouchers(new Set());
      loadData();
    } catch (error) {
      console.error('Delete all failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete all vouchers');
    }
  };

  const generatePDF = async (selectedOnly = false) => {
    try {
      const vouchersToGenerate = selectedOnly && selectedVouchers.size > 0
        ? vouchers.filter((voucher) => selectedVouchers.has(voucher.id))
        : filteredVouchers;

      if (vouchersToGenerate.length === 0) {
        toast.error('No vouchers to generate PDF');
        return;
      }

      const token = localStorage.getItem('token');

      let response;
      if (selectedOnly && selectedVouchers.size > 0) {
        response = await fetch('/api/admin/vouchers/generate-pdf', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            voucherIds: Array.from(selectedVouchers),
            hotelName: printSettings.hotelName,
          }),
        });
      } else {
        const voucherIds = vouchersToGenerate.map((voucher) => voucher.id);
        response = await fetch('/api/admin/vouchers/generate-pdf', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            voucherIds,
            hotelName: printSettings.hotelName,
          }),
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vouchers-${selectedOnly ? 'selected' : 'filtered'}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`PDF downloaded successfully (${vouchersToGenerate.length} vouchers)`);
      setShowPrintModal(false);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'unused':
        return 'px-2 py-0.5 text-xs font-medium rounded bg-green-50 text-green-700';
      case 'used':
        return 'px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700';
      case 'disabled':
        return 'px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-700';
      default:
        return 'px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'unused':
        return <CheckCircle className="h-4 w-4" />;
      case 'used':
        return <AlertCircle className="h-4 w-4" />;
      case 'disabled':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const allPageSelected = paginatedVouchers.length > 0
    && paginatedVouchers.every((voucher) => selectedVouchers.has(voucher.id));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Access Vouchers</h1>
            <p className="text-sm text-gray-600 mt-1">Generate and manage access vouchers</p>
          </div>
          <div className="flex gap-2">
            {paginatedVouchers.length > 0 ? (
              <>
                <button type="button" onClick={toggleSelectAll} className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition flex items-center">
                  {allPageSelected ? <CheckSquare className="h-3.5 w-3.5 mr-1" /> : <Square className="h-3.5 w-3.5 mr-1" />}
                  <span>{allPageSelected ? 'Deselect Page' : 'Select Page'}</span>
                </button>

                {selectedVouchers.size > 0 ? (
                  <button type="button" onClick={bulkDeleteVouchers} className="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition flex items-center">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    <span>Delete ({selectedVouchers.size})</span>
                  </button>
                ) : null}

                <button type="button" onClick={deleteAllVouchers} className="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition flex items-center">
                  <Trash className="h-3.5 w-3.5 mr-1" />
                  <span>Delete Filtered</span>
                </button>

                <button type="button" onClick={() => setShowPrintModal(true)} className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition flex items-center">
                  <Printer className="h-3.5 w-3.5 mr-1" />
                  <span>Print PDF</span>
                </button>
              </>
            ) : null}

            <button type="button" onClick={() => setShowCodes(!showCodes)} className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition flex items-center">
              {showCodes ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
              <span>{showCodes ? 'Hide Codes' : 'Show Codes'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-gray-900 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition flex items-center"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              <span>Generate Vouchers</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search vouchers..."
                className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="unused">Unused</option>
              <option value="used">Used</option>
              <option value="disabled">Disabled</option>
            </select>

            <select
              value={planFilter}
              onChange={(event) => setPlanFilter(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
            >
              <option value="all">All Plans</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="px-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="ml-3 text-sm text-gray-600">Loading vouchers...</p>
          </div>
        </div>
      ) : filteredVouchers.length === 0 ? (
        <div className="px-8">
          <div className="text-center py-12">
            <Ticket className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-900">{vouchers.length === 0 ? 'No vouchers generated yet' : 'No vouchers match your filters'}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {vouchers.length === 0
                ? 'Generate your first batch to get started'
                : 'Try adjusting your search or filters'}
            </p>
            {vouchers.length === 0 ? (
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="mt-4 bg-gray-900 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition inline-flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                <span>Generate First Batch</span>
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="px-8">
          <div className="space-y-3">
            {paginatedVouchers.map((voucher) => (
              <div key={voucher.id} className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="flex min-w-0 gap-3">
                    <button
                      type="button"
                      onClick={() => toggleVoucherSelection(voucher.id)}
                      className="p-1 text-gray-500 hover:text-gray-700 rounded shrink-0"
                      title={selectedVouchers.has(voucher.id) ? 'Deselect voucher' : 'Select voucher'}
                    >
                      {selectedVouchers.has(voucher.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>

                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">{getStatusIcon(voucher.status)}</div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{showCodes ? voucher.code : '••••••••••••'}</code>
                        <span className={getStatusClass(voucher.status)}>
                          <span>{voucher.status}</span>
                        </span>
                        {showCodes ? (
                          <button type="button" onClick={() => copyToClipboard(voucher.code)} className="p-1 text-gray-500 hover:text-gray-700 rounded" title="Copy code">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-600">Plan</p>
                          <p className="text-xs text-gray-900 mt-1">{voucher.plan_name}</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-600">Created</p>
                          <p className="text-xs text-gray-900 mt-1">{new Date(voucher.created_at).toLocaleDateString()}</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-600">Usage</p>
                          <p className="text-xs text-gray-900 mt-1">
                            {voucher.used_by_name ? `Used by ${voucher.used_by_name}` : 'Not redeemed'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button type="button" onClick={() => downloadQR(voucher.id)} className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition flex items-center" title="Download QR Code">
                      <QrCode className="h-3.5 w-3.5 mr-1" />
                      <span>QR</span>
                    </button>

                    {voucher.status === 'unused' && showCodes ? (
                      <button type="button" onClick={() => copyToClipboard(voucher.code)} className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition flex items-center">
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        <span>Copy</span>
                      </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => deleteVoucher(voucher.id, voucher.code)}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition flex items-center"
                      title="Delete Voucher"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalItems={filteredVouchers.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50]}
            itemLabel="vouchers"
          />
        </div>
      )}

      {showModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Ticket className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Generate Access Vouchers</h2>
                    <p className="text-sm text-gray-500">Create access vouchers for users</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Plan</label>
                  <select
                    value={formData.plan_id}
                    onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    required
                  >
                    <option value="">Choose a plan...</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - {plan.up_mbps}/{plan.down_mbps} Mbps
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Vouchers</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.count}
                    onChange={(e) => setFormData({ ...formData, count: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    placeholder="How many vouchers to generate?"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Generate between 1 and 100 vouchers per batch.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for vouchers that never expire.</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <div>
                      <h3 className="text-sm font-medium text-blue-800">Batch Output</h3>
                      <p className="text-xs text-blue-700 mt-0.5">Each voucher includes a unique code, QR asset, and plan association.</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition flex items-center justify-center">
                    <Ticket className="h-4 w-4 mr-2" />
                    <span>Generate Vouchers</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {showPrintModal ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Printer className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Generate Voucher PDF</h2>
                    <p className="text-sm text-gray-500">Create printable voucher cards</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPrintModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hotel or Portal Name</label>
                <input
                  type="text"
                  value={printSettings.hotelName}
                  onChange={(e) => setPrintSettings({ ...printSettings, hotelName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  placeholder="Enter hotel or portal name"
                />
                <p className="text-xs text-gray-500 mt-1">This name appears on each generated voucher card.</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800">PDF Layout</h3>
                    <p className="text-xs text-blue-700 mt-0.5">
                      Six compact voucher cards per page with the QR code, voucher code, and usage instructions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {selectedVouchers.size > 0 ? (
                  <button type="button" onClick={() => generatePDF(true)} className="w-full bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition flex items-center justify-center">
                    <Printer className="h-4 w-4 mr-2" />
                    <span>Print Selected ({selectedVouchers.size})</span>
                  </button>
                ) : null}

                <button type="button" onClick={() => generatePDF(false)} className="w-full px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition flex items-center justify-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Print Filtered ({filteredVouchers.length})</span>
                </button>

                <button type="button" onClick={() => setShowPrintModal(false)} className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Vouchers;
